import type { ComponentMeta } from "@mijalowcode/types";

// 导入所有组件元数据
import { ButtonMeta } from "./antd/Button/meta";
import { InputMeta, InputTextAreaMeta } from "./antd/Input/meta";
import { FormMeta, FormItemMeta, FormListMeta } from "./antd/Form/meta";
import {
  RowMeta,
  ColMeta,
  LayoutMeta,
  HeaderMeta,
  SiderMeta,
  ContentMeta,
  FooterMeta,
} from "./antd/Layout/meta";
import { CardMeta } from "./antd/Card/meta";
import { ProTableMeta } from "./antdPro/ProTable/meta";
import { SchemaFormMeta } from "./antdPro/SchemaForm/meta";
import { FilterColumnsMeta } from "./antdPro/FilterColumns/meta";
import { ModalMeta } from "./antd/Modal/meta";
import { DrawerMeta } from "./antd/Drawer/meta";
import { SelectMeta } from "./antd/Select/meta";
import { SpaceMeta } from "./antd/Space/meta";
import { DesensitizationDisplayMeta } from "./custom/DesensitizationDisplay/meta";

// 新增组件导入
import {
  DatePickerMeta,
  RangePickerMeta,
  TimePickerMeta,
} from "./antd/DatePicker/meta";
import { UploadMeta } from "./antd/Upload/meta";
import { TreeMeta, TreeSelectMeta } from "./antd/Tree/meta";
import { TabsMeta, TabPaneMeta } from "./antd/Tabs/meta";
import { StepsMeta, StepMeta } from "./antd/Steps/meta";
import { TagMeta, CheckableTagMeta } from "./antd/Tag/meta";
import { BadgeMeta, BadgeRibbonMeta } from "./antd/Badge/meta";
import { AvatarMeta, AvatarGroupMeta } from "./antd/Avatar/meta";
import { DropdownMeta, DropdownButtonMeta } from "./antd/Dropdown/meta";
import { BreadcrumbMeta } from "./antd/Breadcrumb/meta";
import { PaginationMeta } from "./antd/Pagination/meta";
import { SwitchMeta } from "./antd/Switch/meta";
import { RadioMeta, RadioGroupMeta } from "./antd/Radio/meta";
import { CheckboxMeta, CheckboxGroupMeta } from "./antd/Checkbox/meta";
import { CascaderMeta } from "./antd/Cascader/meta";
import { InputNumberMeta } from "./antd/InputNumber/meta";
import { TableMeta } from "./antd/Table/meta";
// Typography 组件导入
import {
  TypographyTitleMeta,
  TypographyParagraphMeta,
  TypographyTextMeta,
} from "./antd/Typography/meta";

// 新增组件元数据导入
import { DescriptionsMeta } from "./antd/Descriptions/meta";
import { AlertMeta } from "./antd/Alert/meta";
import { ProgressMeta } from "./antd/Progress/meta";
import { DividerMeta } from "./antd/Divider/meta";
import { EmptyMeta } from "./antd/Empty/meta";
import { SpinMeta } from "./antd/Spin/meta";
import { StatisticMeta } from "./antd/Statistic/meta";
import { TimelineMeta, TimelineItemMeta } from "./antd/Timeline/meta";
import { ResultMeta } from "./antd/Result/meta";
import {
  TooltipMeta,
  PopconfirmMeta,
  SkeletonMeta,
} from "./antd/Tooltip/meta";
import { PopoverMeta } from "./antd/Popover/meta";
import { ListMeta, ListItemMeta } from "./antd/List/meta";
import { ScenarioFlowBoardMeta } from "./custom/scenario/ScenarioFlowBoard/meta";
import { ScenarioFlowSceneMeta } from "./custom/scenario/ScenarioFlowScene/meta";
import { ScenarioFlowStepMeta } from "./custom/scenario/ScenarioFlowStep/meta";

/**
 * 组件元数据映射表: componentName → ComponentMeta
 */
export const componentMetaMap: Record<string, ComponentMeta> = {
  // 通用组件
  Button: ButtonMeta,
  Space: SpaceMeta,
  DesensitizationDisplay: DesensitizationDisplayMeta,

  // 布局组件
  Row: RowMeta,
  Col: ColMeta,
  Card: CardMeta,

  // 表单组件
  Input: InputMeta,
  "Input.TextArea": InputTextAreaMeta,
  Form: FormMeta,
  "Form.Item": FormItemMeta,
  "Form.List": FormListMeta,
  Select: SelectMeta,

  // 数据展示
  ProTable: ProTableMeta,
  Table: TableMeta,

  // 表单增强
  SchemaForm: SchemaFormMeta,
  FilterColumns: FilterColumnsMeta,

  // 反馈组件
  Modal: ModalMeta,
  Drawer: DrawerMeta,

  // ============ 新增组件 ============

  // 日期时间组件
  DatePicker: DatePickerMeta,
  "DatePicker.RangePicker": RangePickerMeta,
  TimePicker: TimePickerMeta,

  // 上传组件
  Upload: UploadMeta,

  // 树形组件
  Tree: TreeMeta,
  TreeSelect: TreeSelectMeta,

  // 标签页组件
  Tabs: TabsMeta,
  "Tabs.TabPane": TabPaneMeta,

  // 步骤条组件
  Steps: StepsMeta,
  "Steps.Step": StepMeta,

  // 标签组件
  Tag: TagMeta,
  "Tag.CheckableTag": CheckableTagMeta,

  // 徽标组件
  Badge: BadgeMeta,
  "Badge.Ribbon": BadgeRibbonMeta,

  // 头像组件
  Avatar: AvatarMeta,
  "Avatar.Group": AvatarGroupMeta,

  // 下拉菜单组件
  Dropdown: DropdownMeta,
  "Dropdown.Button": DropdownButtonMeta,

  // 面包屑组件
  Breadcrumb: BreadcrumbMeta,

  // 分页组件
  Pagination: PaginationMeta,

  // 开关组件
  Switch: SwitchMeta,

  // 单选组件
  Radio: RadioMeta,
  "Radio.Group": RadioGroupMeta,

  // 复选组件
  Checkbox: CheckboxMeta,
  "Checkbox.Group": CheckboxGroupMeta,

  // 级联选择器
  Cascader: CascaderMeta,

  // 数字输入框
  InputNumber: InputNumberMeta,

  // 布局组件
  Layout: LayoutMeta,
  Header: HeaderMeta,
  Sider: SiderMeta,
  Content: ContentMeta,
  Footer: FooterMeta,

  // Typography 组件
  "Typography.Title": TypographyTitleMeta,
  "Typography.Paragraph": TypographyParagraphMeta,
  "Typography.Text": TypographyTextMeta,

  // 新增组件
  Descriptions: DescriptionsMeta,
  Alert: AlertMeta,
  Progress: ProgressMeta,
  Divider: DividerMeta,
  Empty: EmptyMeta,
  Spin: SpinMeta,
  Statistic: StatisticMeta,
  Timeline: TimelineMeta,
  "Timeline.Item": TimelineItemMeta,
  Result: ResultMeta,
  Tooltip: TooltipMeta,
  Popconfirm: PopconfirmMeta,
  Popover: PopoverMeta,
  Skeleton: SkeletonMeta,
  List: ListMeta,
  "List.Item": ListItemMeta,
  ScenarioFlowBoard: ScenarioFlowBoardMeta,
  ScenarioFlowScene: ScenarioFlowSceneMeta,
  ScenarioFlowStep: ScenarioFlowStepMeta,
};

/**
 * 获取组件元数据
 */
export function getComponentMeta(componentName: string): ComponentMeta | null {
  return componentMetaMap[componentName] || null;
}

/**
 * 获取所有组件元数据列表
 */
export function getAllComponentMetas(): ComponentMeta[] {
  return Object.values(componentMetaMap);
}

/**
 * 注册自定义组件元数据
 */
export function registerComponentMeta(meta: ComponentMeta): void {
  componentMetaMap[meta.componentName] = meta;
}
